/**
 * PackageQuickViewModal — floating, draggable and resizable modal
 * that shows PackagePreviewPanel for a package. Used as a "quick view"
 * so users can inspect a package without leaving their current page.
 *
 * Drag: press-hold on the top bar and move.
 * Resize: press-hold the bottom-right handle and move.
 * Escape: close.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Move, GripHorizontal } from "lucide-react";
import PackagePreviewPanel from "./PackagePreviewPanel";

const MIN_W = 420;
const MIN_H = 320;

export default function PackageQuickViewModal({ pid, onClose }) {
  const [box, setBox] = useState(() => {
    // Center-ish initial position
    const w = Math.min(640, window.innerWidth - 40);
    const h = Math.min(560, window.innerHeight - 60);
    return {
      x: Math.max(20, (window.innerWidth - w) / 2),
      y: Math.max(20, (window.innerHeight - h) / 2),
      w, h,
    };
  });
  const dragRef = useRef(null); // {mode: 'drag'|'resize', ox, oy, startBox}

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onPointerMove = (e) => {
    const s = dragRef.current;
    if (!s) return;
    if (s.mode === "drag") {
      const nx = Math.max(0, Math.min(window.innerWidth - 60, s.startBox.x + (e.clientX - s.ox)));
      const ny = Math.max(0, Math.min(window.innerHeight - 40, s.startBox.y + (e.clientY - s.oy)));
      setBox((b) => ({ ...b, x: nx, y: ny }));
    } else if (s.mode === "resize") {
      const nw = Math.max(MIN_W, Math.min(window.innerWidth - 20, s.startBox.w + (e.clientX - s.ox)));
      const nh = Math.max(MIN_H, Math.min(window.innerHeight - 20, s.startBox.h + (e.clientY - s.oy)));
      setBox((b) => ({ ...b, w: nw, h: nh }));
    }
  };
  const onPointerUp = () => {
    dragRef.current = null;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };
  const startDrag = (e) => {
    dragRef.current = { mode: "drag", ox: e.clientX, oy: e.clientY, startBox: { ...box } };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };
  const startResize = (e) => {
    e.stopPropagation();
    dragRef.current = { mode: "resize", ox: e.clientX, oy: e.clientY, startBox: { ...box } };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "nwse-resize";
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  return createPortal(
    <>
      {/* Soft backdrop (click to close). Uses low opacity so users can
          still see context underneath. */}
      <div className="fixed inset-0 z-[190] bg-background/30 backdrop-blur-[1px]"
           onClick={onClose}
           data-testid="pkg-quickview-backdrop" />
      <div className="fixed z-[200] bg-card border border-border shadow-2xl flex flex-col overflow-hidden rounded-none"
           style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
           data-testid="pkg-quickview-modal">
        {/* Drag handle bar */}
        <div className="flex items-center gap-2 px-2 py-1 bg-muted/60 border-b border-border cursor-grab active:cursor-grabbing select-none"
             onPointerDown={startDrag}
             data-testid="pkg-quickview-drag-handle">
          <Move className="w-3 h-3 text-muted-foreground" />
          <div className="flex-1 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Quick view · Package
          </div>
          <button onClick={onClose}
                  className="p-0.5 hover:bg-destructive/20 hover:text-destructive rounded-none"
                  data-testid="pkg-quickview-close"
                  aria-label="Close quick view">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <PackagePreviewPanel pid={pid} onNavigate={onClose} />
        </div>
        {/* Resize handle — bottom right */}
        <div
          onPointerDown={startResize}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize flex items-end justify-end p-0.5 text-muted-foreground/60 hover:text-primary"
          data-testid="pkg-quickview-resize"
          title="Drag to resize">
          <GripHorizontal className="w-3 h-3 rotate-45" />
        </div>
      </div>
    </>,
    document.body,
  );
}
